import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-video-hub-card.scss';
import { trackHubComponentImpression, trackHubViewClick } from 'Analytics/video-detail-events';
import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-profile-image/vm-profile-image';
import 'Components/vm-card/vm-card';

/**
 * A presentational channel "hub" card: channel logo, name, and a "Visit Hub" CTA,
 * with an optional `description` slot for body content (e.g. a related-content list).
 *
 * The whole header links to the hub: `channelUrl` is passed to vm-card's `href`, so vm-card
 * wraps its body in a full-cover link (keyboard-focusable, open-in-new-tab). The "Visit Hub"
 * button is therefore decorative — the overlay carries the navigation — and the `description`
 * sits outside the overlay so links inside it stay clickable.
 *
 * This component is breakpoint-agnostic — it always renders the card chrome. Callers
 * that need responsive behaviour (card on desktop, bare content on mobile) should use
 * `vm-video-hub-related`, which composes this component.
 *
 * @slot description - Optional. Body content rendered below the card header.
 */
@customElement('vm-video-hub-card')
export class VmVideoHubCard extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'channel-name' }) channelName = '';
    @property({ type: String, attribute: 'channel-image-src' }) channelImageSrc = '';
    @property({ type: String, attribute: 'channel-image-alt' }) channelImageAlt = '';
    @property({ type: String, attribute: 'channel-url' }) channelUrl = '';
    /** The qualifying channel's slug, reported as `channel_id` on the hub Mixpanel events. */
    @property({ type: String, attribute: 'channel-slug' }) channelSlug = '';

    private _impressionObserver?: IntersectionObserver;

    override connectedCallback() {
        super.connectedCallback();

        // Fire HubComponentImpression when the card scrolls into view. The mobile and desktop
        // hubs are separate components, but trackHubComponentImpression is deduped per page, so
        // only whichever card the user actually sees emits the event.
        this._impressionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                this._impressionObserver?.disconnect();
                trackHubComponentImpression(this.channelSlug || null);
            });
        });
        this._impressionObserver.observe(this);

        this.addEventListener('click', this._onClick);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._impressionObserver?.disconnect();
        this.removeEventListener('click', this._onClick);
    }

    // The whole card header navigates to the hub via vm-card's overlay anchor (href === channelUrl).
    // Body content slotted into `description` (e.g. related-video cards on desktop) links elsewhere,
    // so match the hub link's href to fire HubViewClick only for the "Visit Hub" navigation.
    private _onClick = (event: MouseEvent) => {
        const navigatedToHub = event
            .composedPath()
            .some((el) => el instanceof HTMLAnchorElement && el.getAttribute('href') === this.channelUrl);

        if (navigatedToHub) trackHubViewClick(this.channelSlug || null);
    };

    override render() {
        // Only forward the description slot when there's slotted body content. Otherwise
        // vm-card would treat the forwarded <slot> as a description child and render its
        // empty bordered/padded region below the header (visible on the body-less mobile card).
        const hasBody = !!this.querySelector(':scope > [slot="description"]');

        return html`
            <vm-card href=${this.channelUrl} link-label="Visit ${this.channelName} hub">
                <vm-profile-image
                    slot="avatar"
                    class="vm-video-hub-card__channel-logo"
                    userName=${this.channelName}
                    imageSrc=${this.channelImageSrc}
                    imageAlt=${this.channelImageAlt || this.channelName}
                    fit="contain"
                ></vm-profile-image>
                <span slot="text" class="vm-video-hub-card__channel-name">${this.channelName}</span>
                <vm-button
                    variant="ghost"
                    color-scheme="neutral"
                    size="sm"
                    slot="cta"
                    class="vm-video-hub-card__channel-cta"
                    >Visit Hub</vm-button
                >
                ${hasBody ? html`<slot name="description" slot="description"></slot>` : nothing}
            </vm-card>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-hub-card': VmVideoHubCard;
    }
}
